import { fetchWithOptions } from 'Utility/fetch';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { mapSessionDataToDevices, mapSessionDataToSettings, mapSessionDataToTreatmentResponses } from './util';
import type { TLink, TPatient, TSessionData } from 'Routes/Treatment/Document/Form/PAPTherapy/interfaces';
import type { IReadonlyDocument } from './readonlyGlobals';

/**
 * Fetch patient info by ECN from AVX
 */
export const fetchAvxPatientInfo = (ecn: string): Promise<TPatient | null> => {
  return fetchWithOptions(`/api/resmed/v1/patient/${ecn}`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(async res => {
      if (res.status === 200) {
        return (await res.json()) as TPatient;
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient info fetch'));
      return null;
    });
};

/**
 * Fetch neuro->AVX patient link information
 */
export const fetchAvxPatientLink = (): Promise<Array<TLink> | null> => {
  return fetchWithOptions(`/api/resmed/v1/link`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(async res => {
      if (res.status === 200) {
        return (await res.json()) as Array<TLink>;
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient link fetch'));
      return null;
    });
};

/**
 * Fetch patients device session data from AVX and store session data to neuro, also updates patient link lastSync state
 */
export const syncAvxSessionData = (): Promise<boolean> => {
  return fetchWithOptions(`/api/resmed/v1/sync`, { neurojwt: parseJWTFromCookie() }, { method: 'POST' })
    .then(res => {
      if (res.status === 200) {
        return true;
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX session data sync'));
      return false;
    });
};

/**
 * Fetch patients device session data from neuro
 */
export const fetchAvxSessionData = (): Promise<Array<TSessionData> | null> => {
  return fetchWithOptions(`/api/resmed/v1/sessions`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX session data fetch'));
      return null;
    });
};

/**
 * Combined function that fetches and maps/converts AVX session data
 */
export const getAvxSessionData = (): Promise<Array<IReadonlyDocument>> => {
  return fetchAvxSessionData()
    .then(res => {
      // Map session data to device/setting/response data used by UI
      if (Array.isArray(res) && res.length > 0) {
        // Mapping logic requires data to be sorted earliest-latest
        const sortedSessionData = res
          .filter(s => s.sessionDate)
          .sort((a, b) => new Date(a.sessionDate!).getTime() - new Date(b.sessionDate!).getTime());

        // In most cases, UI requires data to be sorted latest-earliest, so reverse the order here
        return [
          ...mapSessionDataToTreatmentResponses(sortedSessionData),
          ...mapSessionDataToSettings(sortedSessionData),
          ...mapSessionDataToDevices(sortedSessionData),
        ].toReversed();
      } else return [];
    })
    .catch(() => []);
};
